/**
 * @fileoverview Modal de gestão dos contatos de uma lista salva (ContactSegment).
 * Resolve a truncagem do painel lateral estreito: aqui há largura pra nome
 * completo + número, busca por termo, alternância Ativos/Excluídos e ações de
 * excluir/restaurar por contato. Reaproveita o hook useContactSegments.
 */

import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogContent,
  IconButton,
  CircularProgress,
} from "@material-ui/core";
import { X, Users, Search, Trash2, RotateCcw } from "lucide-react";
import useContactSegments from "../../../../../hooks/useContactSegments";

const SEARCH_DEBOUNCE_MS = 400;

const dialogPaperStyle = {
  background: "#0f1115",
  border: "1px solid rgba(255,255,255,0.08)",
  color: "rgba(255,255,255,0.88)",
  maxWidth: 720,
  width: "100%",
};

const headerStyle = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "14px 18px",
  borderBottom: "1px solid rgba(255,255,255,0.08)",
};

const inputStyle = {
  width: "100%",
  padding: "8px 10px 8px 32px",
  borderRadius: 8,
  border: "1px solid rgba(255,255,255,0.1)",
  background: "rgba(255,255,255,0.04)",
  color: "rgba(255,255,255,0.9)",
  fontSize: 13,
  outline: "none",
  boxSizing: "border-box",
};

const tabBtn = (active, color) => ({
  flex: 1,
  padding: "8px 0",
  fontSize: 12,
  fontWeight: 600,
  cursor: "pointer",
  textAlign: "center",
  borderRadius: 8,
  border: active
    ? `1px solid ${color.border}`
    : "1px solid transparent",
  background: active ? color.bg : "transparent",
  color: active ? color.fg : "rgba(255,255,255,0.55)",
  userSelect: "none",
  transition: "background 120ms ease",
});

const ContactSegmentManagerModal = ({
  open,
  onClose,
  segmentId,
  segmentName,
  toastError,
}) => {
  const { listSegmentContacts, excludeContact, restoreContact } =
    useContactSegments();

  const [mode, setMode] = useState("active"); // 'active' | 'excluded'
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [contacts, setContacts] = useState([]);
  const [count, setCount] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [excludedCount, setExcludedCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);

  // debounce da busca
  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [searchInput]);

  const fetchPage = useCallback(
    async (targetPage) => {
      if (!segmentId) return;
      setLoading(true);
      try {
        const data = await listSegmentContacts(segmentId, {
          pageNumber: targetPage,
          searchParam: search,
          mode: mode === "excluded" ? "excluded" : undefined,
        });
        setContacts((prev) =>
          targetPage === 1 ? data.contacts : [...prev, ...data.contacts]
        );
        setCount(data.count || 0);
        setHasMore(!!data.hasMore);
        setExcludedCount(data.excludedCount || 0);
      } catch (err) {
        if (toastError) toastError(err);
      } finally {
        setLoading(false);
      }
    },
    [segmentId, search, mode, listSegmentContacts, toastError]
  );

  // (re)carrega página 1 quando abre, ou quando muda lista/modo/busca
  useEffect(() => {
    if (!open || !segmentId) return;
    setPage(1);
    setContacts([]);
    fetchPage(1);
  }, [open, segmentId, mode, search, fetchPage]);

  // reset de estado ao trocar de lista ou fechar
  const prevSegmentRef = useRef(segmentId);
  useEffect(() => {
    if (prevSegmentRef.current !== segmentId) {
      prevSegmentRef.current = segmentId;
      setMode("active");
      setSearchInput("");
      setSearch("");
    }
  }, [segmentId]);

  const handleExclude = async (contactId) => {
    setBusyId(contactId);
    try {
      const data = await excludeContact(segmentId, contactId);
      setExcludedCount(data.excludedCount ?? excludedCount + 1);
      setContacts((prev) => prev.filter((c) => c.id !== contactId));
      setCount((prev) => Math.max(0, prev - 1));
    } catch (err) {
      if (toastError) toastError(err);
    } finally {
      setBusyId(null);
    }
  };

  const handleRestore = async (contactId) => {
    setBusyId(contactId);
    try {
      const data = await restoreContact(segmentId, contactId);
      setExcludedCount(data.excludedCount ?? Math.max(0, excludedCount - 1));
      setContacts((prev) => prev.filter((c) => c.id !== contactId));
      setCount((prev) => Math.max(0, prev - 1));
    } catch (err) {
      if (toastError) toastError(err);
    } finally {
      setBusyId(null);
    }
  };

  const handleLoadMore = () => {
    const next = page + 1;
    setPage(next);
    fetchPage(next);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth={false}
      PaperProps={{ style: dialogPaperStyle }}
    >
      <div style={headerStyle}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 15, fontWeight: 600 }}>
          <Users size={16} color="#818cf8" />
          <span>Gerenciar contatos</span>
          {segmentName ? (
            <span style={{ fontSize: 12, fontWeight: 500, color: "rgba(255,255,255,0.5)" }}>
              · {segmentName}
            </span>
          ) : null}
        </div>
        <IconButton size="small" onClick={onClose} style={{ color: "rgba(255,255,255,0.7)" }}>
          <X size={18} />
        </IconButton>
      </div>

      <DialogContent style={{ padding: "14px 18px" }}>
        {/* Busca */}
        <div style={{ position: "relative", marginBottom: 10 }}>
          <Search
            size={14}
            style={{
              position: "absolute",
              left: 10,
              top: "50%",
              transform: "translateY(-50%)",
              color: "rgba(255,255,255,0.4)",
            }}
          />
          <input
            style={inputStyle}
            placeholder="Buscar por nome ou número…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
        </div>

        {/* Toggle Ativos / Excluídos */}
        <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
          <div
            style={tabBtn(mode === "active", {
              border: "rgba(99,102,241,0.4)",
              bg: "rgba(99,102,241,0.16)",
              fg: "#c7d2fe",
            })}
            onClick={() => setMode("active")}
          >
            Ativos
          </div>
          <div
            style={tabBtn(mode === "excluded", {
              border: "rgba(239,68,68,0.4)",
              bg: "rgba(239,68,68,0.16)",
              fg: "#fca5a5",
            })}
            onClick={() => setMode("excluded")}
          >
            Excluídos{excludedCount > 0 ? ` (${excludedCount})` : ""}
          </div>
        </div>

        {/* Contagem */}
        <div style={{ fontSize: 12, fontWeight: 600, color: "#a5b4fc", marginBottom: 8 }}>
          {loading && page === 1
            ? "Calculando…"
            : mode === "excluded"
              ? `${count.toLocaleString("pt-BR")} excluído${count === 1 ? "" : "s"}`
              : `${count.toLocaleString("pt-BR")} contato${count === 1 ? "" : "s"}${search ? " (filtrados)" : " na lista"}`}
        </div>

        {/* Lista */}
        <div
          style={{
            maxHeight: "52vh",
            overflowY: "auto",
            border: "1px solid rgba(255,255,255,0.06)",
            borderRadius: 8,
          }}
        >
          {loading && page === 1 ? (
            <div style={{ padding: "24px 0", textAlign: "center" }}>
              <CircularProgress size={18} style={{ color: "#818cf8" }} />
            </div>
          ) : contacts.length === 0 ? (
            <div
              style={{
                padding: "24px 16px",
                textAlign: "center",
                fontSize: 12,
                color: "rgba(255,255,255,0.45)",
              }}
            >
              {mode === "excluded"
                ? "Nenhum contato excluído."
                : search
                  ? "Nenhum contato encontrado para essa busca."
                  : "Nenhum contato corresponde ao filtro desta lista."}
            </div>
          ) : (
            <>
              {contacts.map((c) => {
                const busy = busyId === c.id;
                return (
                  <div
                    key={c.id}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 12,
                      padding: "9px 12px",
                      borderBottom: "1px solid rgba(255,255,255,0.04)",
                    }}
                  >
                    <span
                      style={{
                        flex: 1,
                        fontSize: 13,
                        color: "rgba(255,255,255,0.85)",
                        lineHeight: 1.35,
                        wordBreak: "break-word",
                      }}
                    >
                      {c.name || "(sem nome)"}
                    </span>
                    <span
                      style={{
                        fontFamily: "monospace",
                        fontSize: 12,
                        color: "rgba(255,255,255,0.45)",
                        flexShrink: 0,
                      }}
                    >
                      {c.number}
                    </span>
                    {mode === "excluded" ? (
                      <span
                        onClick={() => !busy && handleRestore(c.id)}
                        title="Restaurar contato"
                        style={{
                          padding: 4,
                          display: "inline-flex",
                          flexShrink: 0,
                          color: busy ? "rgba(255,255,255,0.25)" : "rgba(129,140,248,0.9)",
                          cursor: busy ? "wait" : "pointer",
                        }}
                      >
                        <RotateCcw size={15} />
                      </span>
                    ) : (
                      <span
                        onClick={() => !busy && handleExclude(c.id)}
                        title="Remover desta lista"
                        style={{
                          padding: 4,
                          display: "inline-flex",
                          flexShrink: 0,
                          color: busy ? "rgba(255,255,255,0.25)" : "rgba(239,68,68,0.75)",
                          cursor: busy ? "wait" : "pointer",
                        }}
                      >
                        <Trash2 size={15} />
                      </span>
                    )}
                  </div>
                );
              })}

              {hasMore && (
                <div
                  onClick={!loading ? handleLoadMore : undefined}
                  style={{
                    padding: "10px 0",
                    textAlign: "center",
                    fontSize: 12,
                    fontWeight: 600,
                    color: loading ? "rgba(255,255,255,0.3)" : "#a5b4fc",
                    cursor: loading ? "default" : "pointer",
                    background: "rgba(99,102,241,0.06)",
                  }}
                >
                  {loading
                    ? "Carregando…"
                    : `Carregar mais (${(count - contacts.length).toLocaleString("pt-BR")} restante${count - contacts.length === 1 ? "" : "s"})`}
                </div>
              )}
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ContactSegmentManagerModal;
